import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  LayoutDashboard,
  Search,
  Settings,
  Users,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { AppState } from '../../store';
import { IOrg, OrgStatus, OrgStatusText, Roles } from '../../modules/orgs/interfaces';
import { DeleteCompany, GroupedOrgs } from './index';
import OrgModal from './OrgModal';
import { isAdmin, AdminRoleOnly, isAdminAndMichael } from '../../components/AdminOnly';
import { getInitials } from '../../general/initials';
import { RoleText } from '../Members/components/columns';
import { StyleGuide } from 'styles/StyleGuide';

const colors = [
  StyleGuide.palette.infoDark,
  StyleGuide.palette.info,
  StyleGuide.palette.infoLight,
  StyleGuide.palette.infoLighter,
];
const colorsText = [
  StyleGuide.palette.infoLighter,
  StyleGuide.palette.infoLight,
  StyleGuide.palette.info,
  StyleGuide.palette.infoDark,
];

// Also used by the group stock drawer.
export function getColor(index: number) {
  return colors[index];
}
export function getTextColor(index: number) {
  return colorsText[index];
}

interface IProps {
  data: GroupedOrgs[];
  deleteOrg: (orgId: string) => void;
}

function TableList({ data, deleteOrg }: IProps) {
  const uid = useSelector((state: AppState) => state.user.details.uid);
  const { t, i18n } = useTranslation();
  const searchKey = `depts:last-search:${uid}`;
  const [search, setSearchValue] = useState(() => {
    try {
      return localStorage.getItem(searchKey) || '';
    } catch {
      return '';
    }
  });
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedOrg, setSelectedOrg] = useState<IOrg | null>(null);
  const displayAll = isAdminAndMichael(uid);
  const columnCount = displayAll ? 5 : 4;
  const setSearch = (value: string) => {
    setSearchValue(value);
    setPage(1);
    setCollapsed([]);
    try {
      if (value) localStorage.setItem(searchKey, value);
      else localStorage.removeItem(searchKey);
    } catch {
      // Search still works when browser storage is unavailable.
    }
  };
  const query = search.trim().toLowerCase();
  const sortByTitle = (a: { title: string }, b: { title: string }) =>
    a.title.localeCompare(b.title, i18n.language, { sensitivity: 'base', numeric: true });
  const matches = (title: string) => (title || '').toLowerCase().includes(query);
  const filtered = data
    .flatMap((row): GroupedOrgs[] => {
      if ('orgs' in row) {
        const orgs = row.orgs
          .filter((org) => matches(row.title) || matches(org.title))
          .sort(sortByTitle);
        return orgs.length ? [{ ...row, orgs }] : [];
      }
      return matches(row.title) ? [row] : [];
    })
    .sort(sortByTitle);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;

  const renderOrg = (org: IOrg, nested = false) => {
    const members = org.members || {};
    const memberGroups = [Roles.ADMIN, Roles.SALES, Roles.DEPARTMENT_EXECUTIVE, Roles.CLEANER].map(
      (role) =>
        Object.values(members)
          .filter(
            (member) =>
              member.role === role &&
              (role !== Roles.ADMIN ||
                isAdmin(uid) ||
                (member.email !== 'info@akstotal.dk' && member.email !== 'info@aks2tal.com'))
          )
          .sort((a, b) =>
            (a.name || a.email || '').localeCompare(b.name || b.email || '', i18n.language)
          )
    );
    const created = org.createTime?.toDate?.();
    const status = OrgStatusText[org.status] ? org.status : OrgStatus.DEFAULT;
    const statusClasses = {
      [OrgStatus.DEFAULT]: 'border-border bg-muted text-muted-foreground',
      [OrgStatus.IN_PROGRESS]: 'border-blue-200 bg-blue-50 text-blue-800',
      [OrgStatus.ACTIVE]: 'border-emerald-200 bg-emerald-50 text-emerald-800',
      [OrgStatus.INACTIVE]: 'border-rose-200 bg-rose-50 text-rose-800',
    };
    return (
      <TableRow
        key={`org:${org.id}`}
        className={`grid grid-cols-2 gap-x-3 px-4 py-3 lg:table-row lg:p-0 ${
          nested ? 'bg-muted/10' : ''
        }`}
      >
        <TableCell
          className={`col-span-2 px-0 py-2 lg:py-5 ${nested ? 'lg:pl-12 lg:pr-5' : 'lg:px-5'}`}
        >
          <Link
            to={`/orgs/${org.id}/dashboard`}
            className="inline-flex items-center gap-2 rounded-sm font-medium text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {org.title}
            <ArrowUpRight
              aria-hidden="true"
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
            />
          </Link>
        </TableCell>
        <TableCell className="col-span-2 px-0 lg:px-5">
          <div className="flex flex-wrap items-center gap-2">
            {memberGroups.map(
              (roleMembers, roleIndex) =>
                roleMembers.length > 0 && (
                  <Popover key={roleIndex}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label={`${t('home.table.members')}: ${org.title} – ${roleMembers
                          .map((member) => member.name || member.email)
                          .join(', ')}`}
                        className="flex -space-x-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {roleMembers.slice(0, 3).map((member) => (
                          <span
                            key={member.uid || member.email}
                            className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background text-xs font-medium uppercase"
                            style={{
                              backgroundColor: getColor(roleIndex),
                              color: getTextColor(roleIndex),
                            }}
                          >
                            {getInitials(member.name || member.email)}
                          </span>
                        ))}
                        {roleMembers.length > 3 && (
                          <span className="flex h-8 min-w-[2rem] items-center justify-center rounded-full border-2 border-background bg-muted px-1 text-xs font-medium">
                            +{roleMembers.length - 3}
                          </span>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="max-h-72 overflow-y-auto">
                      <h3 className="mb-3 text-sm font-semibold">
                        <RoleText role={roleMembers[0].role} />
                      </h3>
                      <ul className="m-0 list-none space-y-3 p-0">
                        {roleMembers.map((member) => (
                          <li key={member.uid || member.email} className="break-words text-sm">
                            <div className="font-medium">{member.name || member.email}</div>
                            {member.name && (
                              <div className="text-xs text-muted-foreground">{member.email}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </PopoverContent>
                  </Popover>
                )
            )}
            {!memberGroups.some((members) => members.length) && (
              <span className="text-sm text-muted-foreground">{t('Ingen synlige adgange')}</span>
            )}
          </div>
        </TableCell>
        <TableCell className="px-0 text-sm text-muted-foreground lg:px-5">
          <span className="mr-1 lg:hidden">{t('home.table.createTime')}:</span>
          {created && !Number.isNaN(created.getTime()) ? format(created, 'dd-MM-yyyy') : '—'}
        </TableCell>
        {displayAll && (
          <TableCell className="px-0 lg:px-5">
            <Badge variant="outline" className={`whitespace-nowrap ${statusClasses[status]}`}>
              {t(OrgStatusText[status])}
            </Badge>
          </TableCell>
        )}
        <TableCell className="col-span-2 px-0 lg:px-5">
          <AdminRoleOnly orgId={org.id}>
            <div className="flex flex-wrap items-center gap-1 lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`${t('Indstillinger')}: ${org.title}`}
                onClick={() => setSelectedOrg(org)}
              >
                <Settings aria-hidden="true" />
                {t('Indstillinger')}
              </Button>
              <DeleteCompany title={org.title} deleteOrg={() => deleteOrg(org.id)} />
            </div>
          </AdminRoleOnly>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <section
        aria-label={t('Afdelingsoversigt')}
        className="overflow-hidden rounded-xl border bg-background shadow-sm"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <div className="relative w-full sm:max-w-sm">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground"
            />
            <Input
              aria-label={t('Søg efter afdeling')}
              placeholder={t('Søg efter afdeling eller undergruppe…')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9 pr-9"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0"
                aria-label={t('Ryd søgning')}
                onClick={() => setSearch('')}
              >
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users aria-hidden="true" className="h-4 w-4" />
            {t('Vælg en afdeling for at åbne dashboardet')}
          </span>
        </div>
        {filtered.length === 0 ? (
          <div role="status" className="flex flex-col items-center px-6 py-16 text-center">
            <Search aria-hidden="true" className="mb-4 h-8 w-8 text-muted-foreground" />
            <h2 className="text-base font-semibold">
              {t(search ? 'Ingen afdelinger matcher' : 'Ingen afdelinger endnu')}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(
                search
                  ? 'Prøv et andet navn eller ryd søgningen.'
                  : 'Tilføj en afdeling for at komme i gang.'
              )}
            </p>
            {search && (
              <Button variant="outline" className="mt-4" onClick={() => setSearch('')}>
                {t('Nulstil søgning')}
              </Button>
            )}
          </div>
        ) : (
          <Table className="block lg:table">
            <TableHeader className="hidden bg-muted/40 lg:table-header-group">
              <TableRow className="hover:bg-transparent">
                <TableHead className="px-5">{t('home.table.companyname')}</TableHead>
                <TableHead className="px-5">{t('home.table.members')}</TableHead>
                <TableHead className="px-5">{t('home.table.createTime')}</TableHead>
                {displayAll && <TableHead className="px-5">{t('home.table.status')}</TableHead>}
                <TableHead className="px-5 text-right">{t('Handlinger')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="block lg:table-row-group">
              {filtered.slice(start, start + pageSize).map((row) =>
                'orgs' in row ? (
                  <Fragment key={`subgroup:${row.id}`}>
                    <TableRow className="block bg-muted/50 lg:table-row">
                      <TableCell
                        colSpan={columnCount}
                        className="block px-4 py-3 lg:table-cell lg:px-5"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Button
                            variant="ghost"
                            className="h-auto min-h-[2.25rem] max-w-full justify-start whitespace-normal px-0 text-left"
                            aria-expanded={!collapsed.includes(row.id)}
                            aria-label={`${t('Undergruppe')}: ${row.title}`}
                            onClick={() =>
                              setCollapsed((keys) =>
                                keys.includes(row.id)
                                  ? keys.filter((id) => id !== row.id)
                                  : [...keys, row.id]
                              )
                            }
                          >
                            {collapsed.includes(row.id) ? (
                              <ChevronRight aria-hidden="true" />
                            ) : (
                              <ChevronDown aria-hidden="true" />
                            )}
                            <FolderOpen aria-hidden="true" />
                            <span>{row.title}</span>
                            <Badge variant="secondary">{row.orgs.length}</Badge>
                          </Button>
                          <Button asChild variant="outline" size="sm">
                            <Link
                              to={`/dashboard/sub?subGroupId=${row.id}`}
                              aria-label={`${t('Dashboard')}: ${row.title}`}
                            >
                              <LayoutDashboard aria-hidden="true" />
                              {t('Dashboard')}
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {!collapsed.includes(row.id) && row.orgs.map((org) => renderOrg(org, true))}
                  </Fragment>
                ) : (
                  renderOrg(row)
                )
              )}
            </TableBody>
          </Table>
        )}
        {filtered.length > 0 && (
          <div className="flex flex-col gap-4 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <p role="status" className="text-muted-foreground">
              {t('Viser {{from}}–{{to}} af {{count}} rækker', {
                from: start + 1,
                to: Math.min(start + pageSize, filtered.length),
                count: filtered.length,
              })}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
              >
                <SelectTrigger aria-label={t('Rækker pr. side')} className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 50, 100].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {t('{{count}} pr. side', { count: size })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <nav aria-label={t('Afdelingssider')} className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('Forrige side')}
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft aria-hidden="true" />
                </Button>
                <span className="min-w-[4rem] text-center tabular-nums">
                  {t('{{page}} af {{pages}}', { page: currentPage, pages: pageCount })}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('Næste side')}
                  disabled={currentPage === pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  <ChevronRight aria-hidden="true" />
                </Button>
              </nav>
            </div>
          </div>
        )}
      </section>
      {selectedOrg && (
        <AdminRoleOnly orgId={selectedOrg.id}>
          <OrgModal visible onClose={() => setSelectedOrg(null)} org={selectedOrg} />
        </AdminRoleOnly>
      )}
    </>
  );
}

export default TableList;
